import { useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Grid,
  TextField,
  Button,
  Typography,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  LinearProgress,
  Box,
  Card,
  CardContent,
  Chip,
  Avatar,
  InputAdornment,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PersonIcon from '@mui/icons-material/Person';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SpeedIcon from '@mui/icons-material/Speed';
import RouteIcon from '@mui/icons-material/Route';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PageLayout from '../common/components/PageLayout';
import ManagementMenu from './ManagementMenu';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  filterPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statsCard: {
    height: '100%',
    textAlign: 'center',
  },
}));

const formatDuration = (ms) => {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.floor((ms % 3600000) / 60000);
  return `${hours}h ${minutes}m`;
};

const DriverPerformancePage = () => {
  const { classes } = useStyles();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [drivers, setDrivers] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedDrivers, setSelectedDrivers] = useState([]);
  const [fromDate, setFromDate] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [toDate, setToDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fuelPrice, setFuelPrice] = useState(5.50);

  useEffectAsync(async () => {
    const [driversRes, devicesRes] = await Promise.all([
      fetch('/api/drivers'),
      fetch('/api/devices'),
    ]);
    if (driversRes.ok) {
      setDrivers(await driversRes.json());
    }
    if (devicesRes.ok) {
      setDevices(await devicesRes.json());
    }
  }, []);

  const handleSearch = async () => {
    setLoading(true);
    setResults([]);

    try {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();

      // Get all trips for all devices
      const allTrips = [];
      for (const device of devices) {
        const response = await fetch(`/api/reports/trips?deviceId=${device.id}&from=${from}&to=${to}`, {
          headers: { Accept: 'application/json' },
        });
        if (response.ok) {
          const trips = await response.json();
          trips.forEach((trip) => {
            let tripFuel = trip.spentFuel || 0;
            let fuelEstimated = false;

            // Se não há dados de combustível do sensor, estimar pelo consumo do dispositivo
            if (tripFuel === 0 && device?.attributes?.form?.fuelConsumption) {
              const consumptionKmL = parseFloat(device.attributes.form.fuelConsumption);
              if (consumptionKmL > 0) {
                tripFuel = (trip.distance / 1000) / consumptionKmL;
                fuelEstimated = true;
              }
            }

            allTrips.push({
              ...trip,
              device,
              fuelUsed: tripFuel,
              fuelEstimated,
            });
          });
        }
      }

      // Group trips by driver
      const driverStats = {};

      allTrips.forEach((trip) => {
        const driverId = trip.driverId || 'unknown';
        const driverName = trip.driverName || t('sharedUnknown');

        if (!driverStats[driverId]) {
          driverStats[driverId] = {
            driverId,
            driverName,
            trips: 0,
            totalDistance: 0,
            totalDuration: 0,
            totalFuel: 0,
            speedSum: 0,
            maxSpeed: 0,
            devicesUsed: new Set(),
          };
        }

        const stats = driverStats[driverId];
        stats.trips += 1;
        stats.totalDistance += trip.distance;
        stats.totalDuration += trip.duration;
        stats.totalFuel += trip.fuelUsed || 0;
        stats.speedSum += trip.averageSpeed;
        stats.maxSpeed = Math.max(stats.maxSpeed, trip.maxSpeed);
        stats.devicesUsed.add(trip.device?.name || trip.deviceId);
      });

      // Calculate final stats and filter by selected drivers
      const driverResults = Object.values(driverStats)
        .filter((d) => {
          if (selectedDrivers.length === 0) return d.driverId !== 'unknown';
          return selectedDrivers.some((sd) => sd.id === d.driverId);
        })
        .map((d) => {
          const avgSpeed = d.trips > 0 ? d.speedSum / d.trips : 0;

          // Calculate performance score
          let score = 100;
          // Penalize high average speed
          if (avgSpeed > 80) score -= Math.min(20, (avgSpeed - 80) * 0.5);
          // Penalize very high max speed
          if (d.maxSpeed > 120) score -= Math.min(30, (d.maxSpeed - 120) * 0.3);
          // Bonus for fuel efficiency
          if (d.totalFuel > 0 && d.totalDistance > 0) {
            const consumption = (d.totalFuel / (d.totalDistance / 1000)) * 100;
            if (consumption < 8) score += 10;
            else if (consumption > 15) score -= 10;
          }
          score = Math.max(0, Math.min(100, Math.round(score)));

          return {
            ...d,
            totalDistance: d.totalDistance / 1000,
            avgSpeed: avgSpeed * 1.852,
            maxSpeed: d.maxSpeed * 1.852,
            devicesUsed: Array.from(d.devicesUsed),
            fuelCost: d.totalFuel * fuelPrice,
            performanceScore: score,
          };
        })
        .sort((a, b) => b.performanceScore - a.performanceScore);

      setResults(driverResults);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (results.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Driver Performance');

    worksheet.columns = [
      { header: 'Rank', key: 'rank', width: 8 },
      { header: t('sharedDriver'), key: 'driverName', width: 25 },
      { header: t('reportTrips'), key: 'trips', width: 10 },
      { header: `${t('reportTotalDistance')} (km)`, key: 'totalDistance', width: 18 },
      { header: t('reportDuration'), key: 'duration', width: 15 },
      { header: `${t('reportAverageSpeed')} (km/h)`, key: 'avgSpeed', width: 18 },
      { header: `${t('reportMaximumSpeed')} (km/h)`, key: 'maxSpeed', width: 18 },
      { header: `${t('reportSpentFuel')} (L)`, key: 'fuel', width: 15 },
      { header: `${t('reportFuelCost')} (R$)`, key: 'fuelCost', width: 15 },
      { header: 'Performance Score', key: 'score', width: 18 },
      { header: t('sharedDevice'), key: 'devices', width: 30 },
    ];

    results.forEach((row, index) => {
      worksheet.addRow({
        rank: index + 1,
        driverName: row.driverName,
        trips: row.trips,
        totalDistance: row.totalDistance.toFixed(2),
        duration: formatDuration(row.totalDuration),
        avgSpeed: row.avgSpeed.toFixed(1),
        maxSpeed: row.maxSpeed.toFixed(1),
        fuel: row.totalFuel.toFixed(2),
        fuelCost: row.fuelCost.toFixed(2),
        score: row.performanceScore,
        devices: row.devicesUsed.join(', '),
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `driver_performance_${new Date().toISOString().split('T')[0]}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => {
    if (results.length === 0) return null;
    const totalTrips = results.reduce((acc, r) => acc + r.trips, 0);
    const totalDistance = results.reduce((acc, r) => acc + r.totalDistance, 0);
    const avgScore = results.reduce((acc, r) => acc + r.performanceScore, 0) / results.length;
    const totalFuelCost = results.reduce((acc, r) => acc + r.fuelCost, 0);
    return {
      driverCount: results.length,
      totalTrips,
      totalDistance: totalDistance.toFixed(1),
      avgScore: Math.round(avgScore),
      totalFuelCost: totalFuelCost.toFixed(2),
    };
  }, [results]);

  const getRankSx = (index) => {
    if (index === 0) return { bgcolor: tokens.semantic.warning, color: tokens.text.primary }; // Gold
    if (index === 1) return { bgcolor: tokens.text.muted, color: tokens.text.primary }; // Silver
    if (index === 2) return { bgcolor: tokens.semantic.warning, color: tokens.text.inverse, opacity: 0.8 }; // Bronze
    return {};
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <PageLayout menu={<ManagementMenu />} breadcrumbs={['reportManagement', 'reportDriverPerformance']}>
      <Container maxWidth={false} className={classes.container}>
        {/* Filters */}
        <Paper className={classes.filterPaper}>
          <Typography variant="h6" gutterBottom>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('reportDriverPerformance')}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                multiple
                options={drivers}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                value={selectedDrivers}
                onChange={(e, value) => setSelectedDrivers(value)}
                renderOption={(props, option) => {
                  const { key, ...rest } = props;
                  return (
                    <li {...rest} key={option.id}>
                      {option.name}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('sharedDriver')}
                    size="small"
                    placeholder={selectedDrivers.length === 0 ? t('sharedAll') : ''}
                  />
                )}
                limitTags={2}
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportFrom')}
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('reportTo')}
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <TextField
                size="small"
                type="number"
                label={t('reportFuelPrice')}
                value={fuelPrice}
                onChange={(e) => setFuelPrice(Number(e.target.value) || 0)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">R$/L</InputAdornment>,
                }}
                inputProps={{ step: 0.01, min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                {t('sharedSearch')}
              </Button>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={results.length === 0}
              >
                Excel
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Stats Cards */}
        {stats && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <PersonIcon color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('sharedDrivers')}</Typography>
                  <Typography variant="h4">{stats.driverCount}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <RouteIcon color="info" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTrips')}</Typography>
                  <Typography variant="h4">{stats.totalTrips}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <SpeedIcon color="warning" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalDistance')}</Typography>
                  <Typography variant="h5">{stats.totalDistance} km</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card className={classes.statsCard}>
                <CardContent>
                  <AttachMoneyIcon color="success" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">{t('reportTotalFuelCost')}</Typography>
                  <Typography variant="h5">R$ {stats.totalFuelCost}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Results Table */}
        {results.length > 0 && (
          <Paper>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Rank</TableCell>
                  <TableCell>{t('sharedDriver')}</TableCell>
                  <TableCell align="right">{t('reportTrips')}</TableCell>
                  <TableCell align="right">{t('reportTotalDistance')} (km)</TableCell>
                  <TableCell align="right">{t('reportDuration')}</TableCell>
                  <TableCell align="right">{t('reportSpentFuel')} (L)</TableCell>
                  <TableCell align="right">{t('reportFuelCost')} (R$)</TableCell>
                  <TableCell align="center">Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.driverId}>
                      <TableCell align="center">
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem',
                            margin: 'auto',
                            ...getRankSx(page * rowsPerPage + index),
                          }}
                        >
                          {page * rowsPerPage + index + 1}
                        </Avatar>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          {row.driverName}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{row.trips}</TableCell>
                      <TableCell align="right">{row.totalDistance.toFixed(2)}</TableCell>
                      <TableCell align="right">{formatDuration(row.totalDuration)}</TableCell>
                      <TableCell align="right">{row.totalFuel.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.fuelCost.toFixed(2)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${row.performanceScore}%`}
                          color={getScoreColor(row.performanceScore)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={results.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        )}

        {!loading && results.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 48, opacity: 0.5, mb: 2 }} />
            <Typography color="textSecondary">
              {drivers.length === 0
                ? t('sharedNoDrivers')
                : t('sharedNoData')}
            </Typography>
          </Paper>
        )}
      </Container>
    </PageLayout>
  );
};

export default DriverPerformancePage;
